import { useState, useEffect, useMemo } from "react";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  extractSessionFromUrl,
  setSessionToken,
  setSessionId,
  getSessionToken,
  getSessionId,
  clearDiscount,
} from "@/lib/utils";
import { useDiscountStore } from "@/lib/discount-store";
import { useOrderDetailsStore } from "@/lib/order-details-store";
import { BrandThemeProvider, brandThemeFromRetailerData, brandThemeFromSession } from "@/lib/brand-theme";
import { useSession } from "@/api/hooks";
import { OrderPage } from "@/pages/OrderPage";
import { PaymentPage } from "@/pages/PaymentPage";
import { SuccessPage } from "@/pages/SuccessPage";
import { FailedPage } from "@/pages/FailedPage";
import { ExpressCartPage } from "@/pages/ExpressCartPage";
import { SessionCartPage } from "@/pages/SessionCartPage";
import { takeDraftDiscountHandoff } from "@/lib/express-cart/draft-discounts";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function InvalidSessionUI() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 max-w-md text-center">
        <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
          <svg
            className="w-8 h-8 text-red-600"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
        </div>

        <h1 className="text-xl font-bold text-gray-900 mb-2">Invalid Session</h1>
        <p className="text-gray-600 text-sm">
          This checkout page requires a valid session. Please access this page through the link
          provided to you.
        </p>
      </div>
    </div>
  );
}

function SessionGate({ children }: { children: React.ReactNode }) {
  const [isReady, setIsReady] = useState(false);
  const [hasSession, setHasSession] = useState(false);

  useEffect(() => {
    const sessionData = extractSessionFromUrl();
    const params = new URLSearchParams(window.location.search);
    const hasSessionParams = params.has("session") || params.has("token");
    const storedSessionId = getSessionId();
    const storedToken = getSessionToken();
    const sessionId = sessionData?.sessionId
      ?? (!hasSessionParams ? storedSessionId : null);
    const token = sessionData?.token
      ?? (sessionId && sessionId === storedSessionId ? storedToken : null);
    const hasValidEntry = !hasSessionParams || !!(sessionData?.sessionId && sessionId && token);

    if (sessionData && hasValidEntry && sessionId && token) {
      // Clear any stale discount data from a previous session
      // only when entering a different session, not when returning from its cart.
      if (hasSessionParams && sessionId !== storedSessionId) {
        clearDiscount();
        useDiscountStore.getState().clearAll();
        useOrderDetailsStore.getState().clear();
      }
      if (hasSessionParams) {
        for (const discount of takeDraftDiscountHandoff(sessionId)) {
          useDiscountStore.getState().addDiscount(discount);
        }
      }

      setSessionToken(token);
      setSessionId(sessionId);

      // Clean up URL params
      const url = new URL(window.location.href);
      url.searchParams.delete("session");
      url.searchParams.delete("token");
      window.history.replaceState({}, "", url.toString());
    }

    const sessionValid = hasValidEntry && !!(sessionId && token);
    setHasSession(sessionValid);
    setIsReady(true);

  }, []);

  if (!isReady) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" />
      </div>
    );
  }

  if (!hasSession) {
    return <InvalidSessionUI />;
  }

  return <>{children}</>;
}

function SessionIdSync({ children }: { children: React.ReactNode }) {
  const { sessionId } = useParams<{ sessionId: string }>();

  useEffect(() => {
    if (sessionId) {
      const storedSessionId = getSessionId();
      if (storedSessionId !== sessionId) {
        setSessionId(sessionId);
      }
    }
  }, [sessionId]);

  return <>{children}</>;
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Routes>
          {/* Express cart from a brand link, /cart/{custom_slug}. Outside SessionGate:
              the shopper may not have a session until they edit the cart. */}
          <Route path="/cart/:code" element={<ExpressCartPage />} />
          {/* Express cart for an existing session: the agent's View Cart link,
              /cart?session={sessionId}&token={token}. Also outside SessionGate:
              the checkout session is only stored on Continue. */}
          <Route path="/cart" element={<SessionCartPage />} />
          <Route path="*" element={<SessionRoutes />} />
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

function SessionRoutes() {
  return (
    <SessionGate>
      <SessionBrandThemeProvider>
        <Routes>
          <Route
            path="/order/:sessionId"
            element={
              <SessionIdSync>
                <OrderPage />
              </SessionIdSync>
            }
          />

          {/* Compatibility route; checkout always starts at shipping. */}
          <Route
            path="/express/:sessionId"
            element={
              <SessionIdSync>
                <OrderPage />
              </SessionIdSync>
            }
          />

          <Route
            path="/order/:sessionId/pay"
            element={
              <SessionIdSync>
                <PaymentPage />
              </SessionIdSync>
            }
          />

          <Route
            path="/order/:sessionId/success"
            element={
              <SessionIdSync>
                <SuccessPage />
              </SessionIdSync>
            }
          />

          <Route
            path="/order/:sessionId/failed"
            element={
              <SessionIdSync>
                <FailedPage />
              </SessionIdSync>
            }
          />

          <Route path="/checkout/*" element={<RedirectToOrder />} />

          <Route path="*" element={<RedirectToOrder />} />
        </Routes>
      </SessionBrandThemeProvider>
    </SessionGate>
  );
}

function SessionBrandThemeProvider({ children }: { children: React.ReactNode }) {
  const sessionId = getSessionId();
  const { data, isLoading, error } = useSession(sessionId);
  const theme = useMemo(
    () => data?.session
      ? brandThemeFromSession(data)
      : error && sessionId
        ? brandThemeFromRetailerData(sessionId, null)
        : null,
    [data, error, sessionId],
  );

  return (
    <BrandThemeProvider theme={theme} isLoading={isLoading} error={error instanceof Error ? error.message : null}>
      {children}
    </BrandThemeProvider>
  );
}

function RedirectToOrder() {
  const sessionId = getSessionId();
  if (sessionId) {
    return <Navigate to={`/order/${sessionId}`} replace />;
  }
  return <InvalidSessionUI />;
}

export default App;
